import { Link } from 'react-router-dom';

export default function PrivacyScreen() {
  return (
    <div className="legal">
      <div className="legal__inner">
        <Link to="/" className="legal__back">← ScanRemind</Link>
        <h1>Privacy Policy</h1>
        <p className="legal__updated">Last updated: May 22, 2026</p>

        <p>
          ScanRemind is a tool that helps you turn photos of physical documents — appointment cards,
          bills, flyers, business cards — into entries in your Microsoft 365 calendar or contacts.
          We've designed it to keep your personal data on your own systems, not ours.
        </p>

        <h2>The short version</h2>
        <ul>
          <li>We <strong>do not</strong> require you to create an account.</li>
          <li>We <strong>do not</strong> store the photos you scan.</li>
          <li>We <strong>do not</strong> store the extracted text (dates, names, amounts, etc.).</li>
          <li>We <strong>do not</strong> keep a history of your scans.</li>
          <li>The events and contacts you create live in your Microsoft 365 account — not ours.</li>
          <li>We never see your Microsoft password.</li>
        </ul>

        <h2>What actually happens when you scan something</h2>
        <ol>
          <li>You take a photo in your browser. It stays on your device.</li>
          <li>Your browser sends the photo to our Cloud Function (hosted on Google Firebase) for analysis.</li>
          <li>Our function forwards the photo to Anthropic's Claude AI model to extract structured data (date, time, name, etc.).</li>
          <li>Claude returns the structured data to our function, which returns it to your browser. The photo is discarded.</li>
          <li>You review and edit the data, then tap "Add to calendar" or "Save to contacts."</li>
          <li>Your browser uses Microsoft Graph (the official Microsoft API) to write the event or contact directly into your Microsoft 365 account.</li>
        </ol>

        <h2>Third parties involved</h2>
        <ul>
          <li>
            <strong>Microsoft (Azure / Microsoft 365)</strong> — handles your sign-in, holds the events and
            contacts you create. Subject to{' '}
            <a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noreferrer">Microsoft's Privacy Statement</a>.
          </li>
          <li>
            <strong>Anthropic (Claude API)</strong> — analyzes the photos to extract text and structured data.
            Per Anthropic's commercial terms, they do not train models on data submitted through their API. See{' '}
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer">Anthropic's Privacy Policy</a>.
          </li>
          <li>
            <strong>Google Firebase (Cloud Functions)</strong> — the hosting platform for our API proxy.
            Photos transit through Firebase but are not persisted there. See{' '}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">Firebase Privacy Policy</a>.
          </li>
          <li>
            <strong>Netlify</strong> — hosts the website and landing page. See{' '}
            <a href="https://www.netlify.com/privacy/" target="_blank" rel="noreferrer">Netlify's Privacy Policy</a>.
          </li>
        </ul>

        <h2>Cookies and local storage</h2>
        <p>
          We use your browser's local storage to remember which Microsoft account you've signed in with,
          so you don't have to sign in every time. That's it — no analytics cookies, no tracking pixels,
          no advertising trackers. Clearing your browser data signs you out.
        </p>

        <h2>Microsoft permissions we request</h2>
        <p>When you connect your Microsoft account, we ask permission to:</p>
        <ul>
          <li>Read and write your calendar (so we can add events you scan)</li>
          <li>Read and write your contacts (so we can save business cards)</li>
          <li>Read your basic profile (just your name/email — to show you're signed in)</li>
          <li>Stay signed in (so you don't have to re-authenticate every visit)</li>
        </ul>
        <p>
          We never use these permissions to read other people's data, send email on your behalf, or do
          anything you didn't ask us to do. You can revoke access at any time at{' '}
          <a href="https://myaccount.microsoft.com/Applications" target="_blank" rel="noreferrer">myaccount.microsoft.com/Applications</a>.
        </p>

        <h2>Children's privacy</h2>
        <p>
          ScanRemind is not directed at children under 13 and we do not knowingly collect any
          information from children.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          If we change this policy, we'll update the date at the top. Major changes will be announced
          on the home page.
        </p>

        <h2>Contact</h2>
        <p>
          ScanRemind is a product of <a href="https://vitalintegrators.com" target="_blank" rel="noreferrer">Vital Integrators</a>,
          based in Lafayette, Louisiana. Questions about privacy?
          Email <a href="mailto:support@vitalintegrators.com">support@vitalintegrators.com</a>.
        </p>

        <p style={{ marginTop: 48 }}>
          <Link to="/">← Back to ScanRemind</Link>
        </p>
      </div>
    </div>
  );
}
